// CatalogCard — member-facing port of the client dashboard's catalog card
// (ploy/client src/components/access/CatalogCard.tsx). Per the user's request,
// the Home catalogs use THIS coverflow design rather than the design bundle's
// flat `.catalog-card`.
//
// Adapted for the employee portal:
//   • data shape is MFAccessCatalog (catalog.items[].catalogItemSelectableResources[])
//   • admin-only chrome is dropped — members never manage catalogs and only
//     ever see published ones, so there's no status dot, AI sparkle, generating
//     skeleton, or delete menu. The bottom row is just "Items: N" + a View CTA.
//
// Token note: the client uses borderRadius="container.lg" (16px there). In this
// repo container.lg is 32px, so the outer radius is mapped to component.4xl
// (16px) to keep the rendered card identical to the client.

import {
  Box,
  Flex,
  HStack,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { forwardRef, useEffect, useRef, useState } from "react";
import { FiBox, FiChevronRight } from "react-icons/fi";
import type {
  MFAccessCatalog,
  MFCatalogItem,
  MFCatalogItemSelectableResource,
} from "state/members/access_v2/catalogs";
import { catalogLinkName } from "state/members/access_v2/catalogNames";

/** Types eligible for de-duplication (Slack channels, groups, etc.) */
const DEDUPE_TYPES = ["channel", "group"] as const;

const isDedupeableType = (type: string): boolean =>
  DEDUPE_TYPES.includes(type as (typeof DEDUPE_TYPES)[number]);

/** A catalog item with its own logo, or a single resource. */
type LogoDisplayItem = {
  kind: "logo";
  key: string;
  name: string;
  imageUrl: string;
};

type GroupedDisplayItem = {
  kind: "grouped";
  type: "channel" | "group";
  count: number;
  imageUrl: string;
  resources: MFCatalogItemSelectableResource[];
};

type OverflowDisplayItem = {
  kind: "overflow";
  count: number;
  resources: MFCatalogItemSelectableResource[];
};

type DisplayItem = LogoDisplayItem | GroupedDisplayItem | OverflowDisplayItem;

/** A resource has a valid image only if it's an actual URL — backend rows often
 *  carry a Feather-icon sentinel ("FiBox") rather than a real logo. */
const hasValidImage = (url: string | undefined | null): url is string =>
  !!(
    url &&
    url.trim() &&
    (url.startsWith("http://") || url.startsWith("https://"))
  );

/** A resource's logo: the one set on the catalog item link wins over the
 *  selectable resource's own. */
const resourceImage = (
  resource: MFCatalogItemSelectableResource,
): string | undefined =>
  [resource.imageUrl, resource.selectableResource?.imageUrl].find(
    hasValidImage,
  );

const resourceLogo = (
  resource: MFCatalogItemSelectableResource,
  imageUrl: string,
): LogoDisplayItem => ({
  kind: "logo",
  key: resource.selectableResource?.id ?? "resource",
  name: catalogLinkName(
    resource,
    resource.selectableResource?.name || resource.name,
  ),
  imageUrl,
});

/**
 * Transform the flat resource array into coverflow display items:
 *  - logo cards for non-channel/group items that have a real image
 *  - grouped cards for de-duped channels/groups with at least one image
 *  - one overflow card for everything without an image
 */
const transformToDisplayItems = (
  resources: MFCatalogItemSelectableResource[],
): DisplayItem[] => {
  const logoItems: LogoDisplayItem[] = [];
  const groupedItems: GroupedDisplayItem[] = [];
  const overflowResources: MFCatalogItemSelectableResource[] = [];

  const dedupeGroups = new Map<string, MFCatalogItemSelectableResource[]>();

  for (const resource of resources) {
    const type = resource.selectableResource?.resource?.type;
    const intId = resource.selectableResource?.resource?.intId;
    const imageUrl = resourceImage(resource);

    if (type && intId && isDedupeableType(type)) {
      const key = `${type}:${intId}`;
      const group = dedupeGroups.get(key) ?? [];
      group.push(resource);
      dedupeGroups.set(key, group);
    } else if (imageUrl) {
      logoItems.push(resourceLogo(resource, imageUrl));
    } else {
      overflowResources.push(resource);
    }
  }

  for (const [key, group] of dedupeGroups) {
    const type = key.split(":")[0] as "channel" | "group";

    if (group.length === 1) {
      const resource = group[0];
      const imageUrl = resourceImage(resource);
      if (imageUrl) {
        logoItems.push(resourceLogo(resource, imageUrl));
      } else {
        overflowResources.push(resource);
      }
    } else {
      const imageUrl = group.map(resourceImage).find(Boolean);

      if (imageUrl) {
        groupedItems.push({
          kind: "grouped",
          type,
          count: group.length,
          imageUrl,
          resources: group,
        });
      } else {
        overflowResources.push(...group);
      }
    }
  }

  const result: DisplayItem[] = [...logoItems, ...groupedItems];

  if (overflowResources.length > 0) {
    result.push({
      kind: "overflow",
      count: overflowResources.length,
      resources: overflowResources,
    });
  }

  return result;
};

/**
 * Carousel tiles for a catalog: a catalog item that has its own logo shows as
 * one tile; an item without one falls back to its resources' logos.
 */
export const toCarouselItems = (items: MFCatalogItem[]): DisplayItem[] => {
  const itemLogos: LogoDisplayItem[] = [];
  const resources: MFCatalogItemSelectableResource[] = [];

  for (const item of items) {
    if (hasValidImage(item.imageUrl)) {
      itemLogos.push({
        kind: "logo",
        key: `item-${item.id}`,
        name: item.name,
        imageUrl: item.imageUrl,
      });
    } else {
      resources.push(...(item.catalogItemSelectableResources ?? []));
    }
  }

  return [...itemLogos, ...transformToDisplayItems(resources)];
};

/** Card entrance — fade up. */
export const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/** Coverflow item entrance — slide in from the right. */
const resourceSlideIn = keyframes`
  0%   { opacity: 0; transform: translateX(40px); }
  100% { opacity: 1; transform: translateX(0); }
`;

/** Delay before showing a hover tooltip so the card animation can settle. */
const TOOLTIP_DELAY = 200;

const COVERFLOW_SHADOW =
  "0px -1px 1px 0px rgba(0,0,0,0.08), -1px 0px 2px 0px rgba(0,0,0,0.06), 1px 0px 2px 0px rgba(0,0,0,0.06)";

const CARD_SHADOW =
  "0px 0px 1px 0px rgba(0,0,0,0.32), 0px 0px 2px 0px rgba(0,0,0,0.08), 0px 1px 3px 0px rgba(0,0,0,0.08)";

// A single coverflow item's hover transform: lift the hovered card, and push
// its neighbours away (left neighbours travel further to clear the overlap).
const coverflowTransform = (
  isHovered: boolean,
  index: number,
  hoveredIndex: number | null,
): string => {
  if (isHovered) return "scale(1.1) translateY(-4px)";
  if (hoveredIndex !== null) {
    if (index < hoveredIndex) return "translateX(-50px)";
    if (index > hoveredIndex) return "translateX(8px)";
  }
  return "scale(1)";
};

interface CoverflowCommon {
  isHovered: boolean;
  index: number;
  totalCount: number;
  hoveredIndex: number | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  animateIn?: boolean;
  animationDelay?: number;
}

/** The shared coverflow tile shell (image / fallback box, transforms, z-index).
 *  forwardRef so the parent Tooltip / PopoverTrigger can anchor to the DOM node. */
const CoverflowTile = forwardRef<
  HTMLDivElement,
  CoverflowCommon & { cursor?: string; children: React.ReactNode }
>(
  (
    {
      isHovered,
      index,
      totalCount,
      hoveredIndex,
      onMouseEnter,
      onMouseLeave,
      animateIn = false,
      animationDelay = 0,
      cursor = "pointer",
      children,
      ...rest
    },
    ref,
  ) => (
    <Box
      ref={ref}
      flexShrink={0}
      bg="bg.container"
      p="sm"
      pb="md"
      borderTopRadius="component.md"
      borderBottomRadius={0}
      boxShadow={COVERFLOW_SHADOW}
      cursor={cursor}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      transform={coverflowTransform(isHovered, index, hoveredIndex)}
      transition="all 0.2s cubic-bezier(0.33, 1, 0.68, 1)"
      zIndex={totalCount - index}
      position="relative"
      mr={isHovered ? "sm" : "-30px"}
      opacity={animateIn ? 0 : 1}
      animation={
        animateIn
          ? `${resourceSlideIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${animationDelay}ms forwards`
          : undefined
      }
      {...rest}
    >
      {children}
    </Box>
  ),
);
CoverflowTile.displayName = "CoverflowTile";

/** A 40px logo image, or a FiBox fallback when the image is missing/broken. */
const TileImage = ({ imageUrl }: { imageUrl?: string }) => {
  const [imageError, setImageError] = useState(false);
  if (imageUrl && !imageError) {
    return (
      <Box
        as="img"
        src={imageUrl}
        w="40px"
        h="auto"
        minH="40px"
        maxH="56px"
        borderRadius="component.sm"
        objectFit="contain"
        display="block"
        onError={() => setImageError(true)}
      />
    );
  }
  return (
    <Flex
      w="40px"
      h="40px"
      align="center"
      justify="center"
      bg="neutral.100"
      borderRadius="component.sm"
    >
      <Icon as={FiBox} boxSize="20px" color="neutral.500" />
    </Flex>
  );
};

/** A delayed tooltip that opens only after the card has settled. */
const useSettledTooltip = (isHovered: boolean) => {
  const [showTooltip, setShowTooltip] = useState(false);
  useEffect(() => {
    if (isHovered) {
      const timer = setTimeout(() => setShowTooltip(true), TOOLTIP_DELAY);
      return () => clearTimeout(timer);
    }
    setShowTooltip(false);
    return undefined;
  }, [isHovered]);
  return showTooltip;
};

const tooltipProps = {
  placement: "top" as const,
  hasArrow: true,
  bg: "neutral.900",
  color: "white",
  fontSize: "sm",
  borderRadius: "component.md",
  px: "sm",
  py: "xs",
};

const LogoCard = ({
  item,
  ...common
}: { item: LogoDisplayItem } & CoverflowCommon) => {
  const { imageUrl, name } = item;
  const showTooltip = useSettledTooltip(common.isHovered);

  return (
    <Tooltip label={name} isOpen={showTooltip} {...tooltipProps}>
      <CoverflowTile {...common}>
        <TileImage imageUrl={imageUrl} />
      </CoverflowTile>
    </Tooltip>
  );
};

const GroupedResourceCard = ({
  item,
  ...common
}: { item: GroupedDisplayItem } & CoverflowCommon) => {
  const showTooltip = useSettledTooltip(common.isHovered);
  const label = `${item.count} ${item.type}s`;

  return (
    <Tooltip label={label} isOpen={showTooltip} {...tooltipProps}>
      <CoverflowTile {...common} cursor="default">
        <TileImage imageUrl={item.imageUrl} />
      </CoverflowTile>
    </Tooltip>
  );
};

const capitalizeAndPluralize = (type: string, count: number): string => {
  const capitalized = type.charAt(0).toUpperCase() + type.slice(1);
  return count === 1 ? capitalized : `${capitalized}s`;
};

const OverflowCard = ({
  item,
  ...common
}: { item: OverflowDisplayItem } & CoverflowCommon) => {
  const groupedByType = item.resources.reduce(
    (acc, resource) => {
      const type = resource.selectableResource?.type ?? "Other";
      (acc[type] = acc[type] ?? []).push(resource);
      return acc;
    },
    {} as Record<string, MFCatalogItemSelectableResource[]>,
  );
  const sortedTypes = Object.keys(groupedByType).sort();

  return (
    <Popover
      trigger="hover"
      placement="top"
      openDelay={TOOLTIP_DELAY}
      strategy="fixed"
    >
      <PopoverTrigger>
        <CoverflowTile {...common} cursor="default">
          <Flex
            w="40px"
            h="40px"
            align="center"
            justify="center"
            bg="neutral.100"
            borderRadius="component.sm"
          >
            <Text fontSize="sm" fontWeight="semibold" color="neutral.600">
              +{item.count}
            </Text>
          </Flex>
        </CoverflowTile>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          bg="neutral.900"
          borderColor="neutral.700"
          borderRadius="component.md"
          maxH="200px"
          overflowY="auto"
          w="auto"
          minW="160px"
          maxW="240px"
        >
          <PopoverBody p="sm">
            <VStack align="stretch" spacing="sm">
              {sortedTypes.map((type) => {
                const resources = [...groupedByType[type]].sort((a, b) => {
                  const nameA = a.selectableResource?.name ?? a.name ?? "";
                  const nameB = b.selectableResource?.name ?? b.name ?? "";
                  return nameA.localeCompare(nameB);
                });
                return (
                  <VStack key={type} align="stretch" spacing="xs">
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="neutral.400"
                    >
                      {capitalizeAndPluralize(type, resources.length)} (
                      {resources.length})
                    </Text>
                    {resources.map((resource, idx) => (
                      <Text
                        key={`${resource.selectableResource?.id ?? "res"}-${idx}`}
                        fontSize="sm"
                        color="white"
                        pl="sm"
                        noOfLines={1}
                      >
                        {catalogLinkName(
                          resource,
                          resource.selectableResource?.name ?? resource.name,
                        )}
                      </Text>
                    ))}
                  </VStack>
                );
              })}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

/** Empty-carousel placeholder — five faint stacked tiles. */
const EmptyCarousel = () => (
  <HStack
    spacing={0}
    pt="sm"
    pl="sm"
    pr="lg"
    pb={0}
    borderBottomWidth="1px"
    borderBottomColor="border.default"
    align="flex-end"
    minH="72px"
  >
    {[...Array(5)].map((_, index) => (
      <Box
        key={index}
        flexShrink={0}
        bg="bg.container"
        p="sm"
        pb="md"
        borderTopRadius="component.md"
        borderBottomRadius={0}
        boxShadow={COVERFLOW_SHADOW}
        mr="-30px"
        zIndex={5 - index}
        position="relative"
      >
        <Flex
          w="40px"
          h="40px"
          align="center"
          justify="center"
          bg="neutral.100"
          borderRadius="component.sm"
        >
          <Icon as={FiBox} boxSize="20px" color="neutral.500" />
        </Flex>
      </Box>
    ))}
  </HStack>
);

const ResourceCarousel = ({ items }: { items: MFCatalogItem[] }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  // Compute "should animate" synchronously on the first render that has items
  // so cards never flash visible before the slide-in kicks in.
  const hasAnimatedRef = useRef(false);

  const displayItems = toCarouselItems(items);

  let shouldAnimate = false;
  if (displayItems.length > 0 && !hasAnimatedRef.current) {
    shouldAnimate = true;
    hasAnimatedRef.current = true;
  }

  if (displayItems.length === 0) return <EmptyCarousel />;

  return (
    <Box
      overflowX="auto"
      overflowY="visible"
      // min-width:0 so the scroll container actually shrinks with its card
      // instead of forcing the card (and its grid column) wider than available.
      minW={0}
      pt="sm"
      pb={0}
      pl="sm"
      borderBottomWidth="1px"
      borderBottomColor="border.default"
      sx={{
        "&::-webkit-scrollbar": { height: "6px" },
        "&::-webkit-scrollbar-track": { bg: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          bg: "neutral.200",
          borderRadius: "full",
        },
        "&::-webkit-scrollbar-thumb:hover": { bg: "neutral.300" },
        scrollbarWidth: "thin",
        scrollbarColor: "var(--chakra-colors-neutral-200) transparent",
      }}
    >
      <HStack spacing={0} align="flex-end" minH="72px" pb={0} pr="lg">
        {displayItems.map((item, index) => {
          const common: CoverflowCommon = {
            isHovered: hoveredIndex === index,
            index,
            totalCount: displayItems.length,
            hoveredIndex,
            onMouseEnter: () => setHoveredIndex(index),
            onMouseLeave: () => setHoveredIndex(null),
            animateIn: shouldAnimate,
            animationDelay: index * 60,
          };
          switch (item.kind) {
            case "logo":
              return (
                <LogoCard
                  // Same selectable resource can appear under multiple catalog
                  // items in one catalog, so the SR id alone isn't unique —
                  // qualify it with the positional index.
                  key={`${item.key}-${index}`}
                  item={item}
                  {...common}
                />
              );
            case "grouped":
              return (
                <GroupedResourceCard
                  key={`grouped-${item.type}-${index}`}
                  item={item}
                  {...common}
                />
              );
            case "overflow":
              return <OverflowCard key="overflow" item={item} {...common} />;
          }
        })}
      </HStack>
    </Box>
  );
};

export interface CatalogCardProps {
  catalog: MFAccessCatalog;
  onView?: () => void;
  animateIn?: boolean;
  animationDelay?: number;
}

/**
 * Coverflow catalog card — resource carousel on top, then catalog name +
 * description, the item count, and a View CTA.
 */
const CatalogCard = ({
  catalog,
  onView,
  animateIn = false,
  animationDelay = 0,
}: CatalogCardProps) => {
  const items = catalog.items ?? [];
  const allSelectableResources = items.flatMap(
    (item) => item.catalogItemSelectableResources ?? [],
  );
  const itemCount = allSelectableResources.length;

  return (
    <VStack
      w="full"
      h="full"
      align="stretch"
      spacing="lg"
      p="lg"
      bg="bg.container"
      borderRadius="component.4xl" /* 16px — client uses container.lg (16px there) */
      boxShadow={CARD_SHADOW}
      overflow="hidden"
      animation={
        animateIn
          ? `${fadeInUp} 0.4s ease-out ${animationDelay}ms both`
          : undefined
      }
    >
      <ResourceCarousel items={items} />

      <VStack align="stretch" spacing="sm">
        {/* PLO-4408: the title is a clickable affordance that opens the catalog,
            mirroring the View CTA — both call onView so they can't diverge. It's
            keyboard-operable (Enter/Space) and exposed as a button to AT. */}
        <Text
          fontSize="lg"
          fontWeight="semibold"
          color="text.body.primary"
          lineHeight="1"
          cursor={onView ? "pointer" : undefined}
          _hover={onView ? { textDecoration: "underline" } : undefined}
          onClick={onView}
          role={onView ? "button" : undefined}
          tabIndex={onView ? 0 : undefined}
          onKeyDown={
            onView
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onView();
                  }
                }
              : undefined
          }
        >
          {catalog.name}
        </Text>
        {catalog.description && (
          <Text
            fontSize="lg"
            color="text.body.tertiary"
            lineHeight="1.5"
            noOfLines={2}
          >
            {catalog.description}
          </Text>
        )}
      </VStack>

      {/* Items count — pushed to the bottom. */}
      <HStack spacing="xs" mt="auto">
        <Text
          fontSize="md"
          fontWeight="medium"
          color="text.body.tertiary"
          lineHeight="1"
        >
          Items:
        </Text>
        <Text
          fontSize="md"
          fontWeight="medium"
          color="text.body.tertiary"
          lineHeight="1"
        >
          {itemCount}
        </Text>
      </HStack>

      <HStack justify="space-between" align="center">
        <HStack
          as="button"
          spacing="xs"
          px="xs"
          h="32px"
          bg="bg.container"
          borderRadius="component.lg"
          boxShadow={CARD_SHADOW}
          cursor="pointer"
          _hover={{ bg: "neutral.50" }}
          transition="all 0.2s"
          onClick={onView}
        >
          <Text
            fontSize="md"
            fontWeight="semibold"
            color="text.body.primary"
            lineHeight="1"
          >
            View
          </Text>
          <Icon as={FiChevronRight} boxSize="16px" color="text.body.primary" />
        </HStack>
      </HStack>
    </VStack>
  );
};

export default CatalogCard;
